import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import i18next from 'i18next';

import { APP_VERSION } from 'constants/appVersion';
import { warehouses } from 'constants/warehouses';

import { NavItemGroup, Zone } from 'types';
import { Order } from 'types/api/orders.type';
import { Season, SeasonConfig } from 'types/api/seasons.type';

import { useGetDropdownOrdersQuery } from 'redux/api/ordersApi';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { setOrders } from 'redux/slices/orders';
import { setQueries } from 'redux/slices/query';
import {
  setBarColor,
  setMarketLine,
  setOrderId,
  setOrderNumber,
  setSeason,
  setShowSold,
  setWarehouse,
  setZone,
} from 'redux/slices/settings';
import {
  getUserPermissions,
  getUserZonesWithSpecial,
  logout,
  setCurrentUser,
  setIsDistributor,
} from 'redux/slices/userData';

import { catalogueDataService, usersDataService } from 'services';

import { AuthContext, LayoutContext } from 'contexts';

import { formatSeasonLabel } from 'helpers/formatSeason';

import { useSeasons } from 'hooks';
import { isPriceZoneSuppressed } from 'hooks/usePriceVisible';

import { routes } from 'data/routes';

import manual_en from 'assets/manual_en.pdf';
import manual_it from 'assets/manual_it.pdf';

const useSettingsData = (): NavItemGroup[] => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();
  const location = useLocation();
  // Menu items that only act (switch language, toggle, open the manual) must leave the page as it
  // is. They render as links, and a link to '#' keeps the path but drops the query string — the
  // product page reads its codes from it, so it came back empty on the next reload.
  const currentPage = `${location.pathname}${location.search}`;

  const user = useAppSelector(({ userData }) => userData.user);
  const zones = useAppSelector(getUserZonesWithSpecial);
  const seasons = useAppSelector((state) => state.userData.user.season);
  const permissions = useAppSelector(getUserPermissions);
  const { season, marketLine, zone, orderNumber, warehouse } = useAppSelector(
    (state) => state.settings
  );
  const isDistributor = useAppSelector((state) => state.userData.isUserDistributor);
  const authContext = useContext(AuthContext);
  const { isOrderModalVisibleSet } = useContext(LayoutContext);
  const { currentSeasonLines, linesList } = useSeasons(seasons, season);
  const showSold = useAppSelector((state) => state.settings.showSold);
  const { t } = useTranslation();
  const language = localStorage.getItem('language');

  // RTK Query: dropdown orders (paginated, infinite scroll on dropdown)
  const canViewOrders = permissions.includes('VIEW_ORDER');
  const [dropdownPage, setDropdownPage] = useState(0);
  const [accumulatedOrders, setAccumulatedOrders] = useState<Order[]>([]);
  const isLoadingMoreRef = useRef(false);

  // Section config: seasons/lines available for the selected warehouse
  const [sectionConfig, setSectionConfig] = useState<SeasonConfig[]>([]);

  useEffect(() => {
    if (!warehouse.code) return;

    catalogueDataService
      .getSectionConfig(warehouse.code)
      .then((res) => setSectionConfig(res.data))
      .catch(() => setSectionConfig([]));
  }, [warehouse.code]);

  // When section-config updates, reset season/marketLine if current selection is no longer valid
  useEffect(() => {
    if (!sectionConfig.length) return;

    const availableSeasonsSet = new Set(sectionConfig.map((c) => c.season));

    if (season && !availableSeasonsSet.has(season)) {
      const firstAvailable = sectionConfig[0];
      const newSeason = firstAvailable.season;
      const newMarketLine = firstAvailable.descEng;

      localStorage.setItem('season', newSeason);
      dispatch(setSeason(newSeason));
      localStorage.setItem('marketLine', newMarketLine);
      dispatch(setMarketLine(newMarketLine));
    }
  }, [sectionConfig]);

  const { data: dropdownData, isFetching: isDropdownFetching } = useGetDropdownOrdersQuery(
    {
      zone: zone || undefined,
      page: dropdownPage,
      season: season || undefined,
      marketingLine: marketLine || undefined,
    },
    { skip: !canViewOrders }
  );

  const hasMoreOrders = dropdownData?.hasMore ?? false;

  // Track fetching state to prevent rapid-fire scroll calls
  useEffect(() => {
    isLoadingMoreRef.current = isDropdownFetching;
  }, [isDropdownFetching]);

  // Accumulate pages into a single list (deduplicated by orderId)
  useEffect(() => {
    if (!dropdownData?.orders) return;
    if (dropdownPage === 0) {
      // First page (initial load or after reset) — replace entirely
      setAccumulatedOrders(dropdownData.orders);
    } else {
      // Subsequent pages — append only new items
      setAccumulatedOrders((prev) => {
        const existingIds = new Set(prev.map((o) => o.orderId));
        const newOrders = dropdownData.orders.filter((o) => !existingIds.has(o.orderId));
        return [...prev, ...newOrders];
      });
    }
  }, [dropdownData, dropdownPage]);

  useEffect(() => {
    setDropdownPage(0);
  }, [zone, season, marketLine]);

  const orders =
    dropdownPage === 0 && dropdownData?.orders && accumulatedOrders.length === 0
      ? dropdownData.orders
      : accumulatedOrders;

  const loadMoreOrders = useCallback(() => {
    if (hasMoreOrders && !isLoadingMoreRef.current) {
      isLoadingMoreRef.current = true;
      setDropdownPage((prev) => prev + 1);
    }
  }, [hasMoreOrders]);

  useEffect(() => {
    i18next.changeLanguage(localStorage.getItem('language') === 'en' ? 'en' : 'it');
    const zoneCode = localStorage.getItem('zone');

    if (zoneCode) {
      dispatch(setZone(zoneCode));
    } else {
      const validZones = [...zones].filter((z) => z.code != null && z.code !== '');
      const sortedZones = validZones.sort((a, b) => a.code.localeCompare(b.code));
      const defaultZone =
        sortedZones.find((z) => z.code === '01')?.code ?? sortedZones[0]?.code ?? '01';
      localStorage.setItem('zone', defaultZone);
      dispatch(setZone(defaultZone));
    }
  }, [zones, zone]);

  const cleanupStorage = (userSeasons: Season[], userZones: Zone[]) => {
    const localSeason = localStorage.getItem('season');
    const localZone = localStorage.getItem('zone');

    if (localSeason && !userSeasons.find((item) => item.season === localSeason)) {
      localStorage.removeItem('season');
      localStorage.removeItem('marketLine');
      dispatch(setSeason(''));
      dispatch(setMarketLine(''));
    }

    if (localZone && !userZones.find((item) => item.code === localZone)) {
      localStorage.removeItem('zone');
      dispatch(setZone(''));
    }
  };

  const warehouseLabelsByCode: Record<string, string> = {
    1: t('nav.catalogue'),
    2: t('catalogue.offSeason'),
    3: t('catalogue.inSeason'),
  };
  const getWarehouseLabel = (item: { code: string; name: string }) =>
    warehouseLabelsByCode[item.code] ?? item.name;

  const listWarehouses = warehouses
    ?.filter((item) => item.code !== warehouse.code)
    .map((item) => ({
      label: getWarehouseLabel(item),
      onClick: () => {
        localStorage.setItem('warehouse', JSON.stringify(item));
        dispatch(setWarehouse(item));
        localStorage.setItem('orderNumber', '');
        localStorage.setItem('orderId', '');
        dispatch(setOrderNumber(''));
        dispatch(setOrderId(''));
        dispatch(setOrders([]));

        const getColorBar = () => {
          switch (item.code) {
            case '1':
              return 'black';
            case '2':
              return 'darkRed';
            case '3':
              return 'colmarBlue';
            default:
              return 'black';
          }
        };

        localStorage.setItem('barColor', getColorBar());
        dispatch(setBarColor(getColorBar()));

        navigate(routes.home);
        toast.success(t('settings.warehouseSelected'));
      },
    }));

  const zonesItems = zones
    .filter((item) => item.code !== zone)
    .map((el: Zone) => ({
      label: el.code,
      onClick: () => {
        localStorage.setItem('zone', el.code);
        dispatch(setZone(el.code));
        localStorage.setItem('orderNumber', '');
        localStorage.setItem('orderId', '');
        dispatch(setOrderNumber(''));
        dispatch(setOrderId(''));
        dispatch(setOrders([]));

        navigate(routes.home);
        toast.success(t('settings.userAreaSelected'));
      },
    }))
    .sort((a, b) => (a.label > b.label ? 1 : -1));

  // Filter seasons and lines based on section-config for the current warehouse
  const availableSeasons = new Set(sectionConfig.map((c) => c.season));
  const availableLinesBySeason = sectionConfig.reduce<Record<string, Set<string>>>(
    (acc, item) => {
      if (!acc[item.season]) acc[item.season] = new Set();
      acc[item.season].add(item.descEng);
      return acc;
    },
    {}
  );

  const filteredLinesList = sectionConfig.length
    ? linesList.filter((item) => availableSeasons.has(item.season))
    : linesList;

  const filteredCatalog =
    sectionConfig.length && currentSeasonLines
      ? currentSeasonLines.catalog.filter((line) => {
          const availableLines = availableLinesBySeason[season];
          if (!availableLines) return false;
          return availableLines.has(line);
        })
      : currentSeasonLines?.catalog;

  const seasonsItems = filteredLinesList
    .filter((item) => item.season !== season)
    .map((item) => ({
      label: formatSeasonLabel(item.season),
      onClick: () => {
        localStorage.setItem('season', item.season);
        dispatch(setSeason(item.season));

        const availableLines = availableLinesBySeason[item.season];
        const filteredCatalogForSeason = availableLines
          ? item.catalog.filter((c) => availableLines.has(c))
          : item.catalog;

        const seasonMarketLine =
          filteredCatalogForSeason.find((catalogItem) => catalogItem === 'ORIGINALS') ||
          filteredCatalogForSeason[0];

        localStorage.setItem('marketLine', seasonMarketLine);
        dispatch(setMarketLine(seasonMarketLine));
        localStorage.setItem('orderNumber', '');
        localStorage.setItem('orderId', '');
        dispatch(setOrderNumber(''));
        dispatch(setOrderId(''));
        dispatch(setOrders([]));

        navigate(routes.home);
        toast.success(t('settings.seasonSelected'));
      },
    }));

  const linesItems = (filteredCatalog || [])
    .map((item) => ({
      label: item,
      onClick: () => {
        localStorage.setItem('marketLine', item);
        dispatch(setMarketLine(item.toLocaleLowerCase()));

        localStorage.setItem('orderNumber', '');
        localStorage.setItem('orderId', '');
        dispatch(setOrderNumber(''));
        dispatch(setOrderId(''));
        dispatch(setOrders([]));

        navigate(routes.home);
        toast.success(t('settings.marketLineSelected'));
      },
    }))
    .filter((item) => item.label !== marketLine);

  const ordersItems = orders
    ?.filter((item) => item.status !== 'SEND')
    .map((item) => {
      const itemOrderId = String(item.orderId);
      const itemOrderNumber = String(item.orderNumber);
      return {
        label: `${t('order.orderNumberShort')}${itemOrderNumber}`,
        onClick: () => {
          localStorage.setItem('orderNumber', itemOrderNumber);
          localStorage.setItem('orderId', itemOrderId);
          dispatch(setOrderNumber(itemOrderNumber));
          dispatch(setOrderId(itemOrderId));
          toast.success(t('order.changedSuccessfully'));
          // OrderContext keys its data off the :id route param, not this
          // Redux state, so switching the active order from here must also
          // navigate — otherwise the order page silently keeps rendering
          // the previously loaded order's data.
          navigate(routes.singleOrder(itemOrderId));
        },
      };
    });

  const getFileUrl = (lang: string | null) => {
    switch (lang) {
      case 'en':
        return manual_en;
      case 'it':
        return manual_it;
      default:
        return manual_it;
    }
  };
  useEffect(() => {
    usersDataService.getCurrentUser().then((res) => {
      cleanupStorage(res.data.season, res.data.zone);
      dispatch(setCurrentUser(res.data));
      dispatch(setIsDistributor());
    });
  }, []);

  return [
    {
      id: 1,
      items: [
        {
          order: 1,
          label: user?.name && user?.surname ? `${user.name} ${user.surname}` : user.email,
          iconLeft: 'FiSettings',
          iconRight: 'FiChevronDown',
          menuAlign: 'left',
          wideTrigger: true,
          list: [
            {
              id: 1,
              items: [
                {
                  label: t('settings.showSold'),
                  to: currentPage,
                  hasSwitchButton: true,
                  isOn: showSold,
                  onChange: (val) => dispatch(setShowSold(val)),
                },
                {
                  label: t('nav.settings'),
                  iconLeft: 'FiSettings',
                  iconRight: 'FiChevronDown',
                  to: currentPage,
                  subItems:
                    isDistributor && !isPriceZoneSuppressed(zone)
                      ? [
                          {
                            label: 'prices',
                            to: routes.prices,
                          },
                        ]
                      : undefined,
                },
                {
                  label: t('settings.manual'),
                  to: currentPage,
                  onClick: () => {
                    window.open(getFileUrl(language), '_blank', 'noopener,noreferrer');
                  },
                },
                {
                  label: t('nav.logout'),
                  iconLeft: 'FiLogOut',
                  to: '#',
                  onClick: () => {
                    dispatch(setQueries({}));
                    dispatch(logout());
                    authContext.signOut();
                    navigate('/');
                  },
                },
                {
                  label: APP_VERSION,
                  disabled: true,
                },
              ],
            },
          ],
        },
        {
          order: 2,
          label: getWarehouseLabel(warehouse),
          iconRight: 'FiChevronDown',
          hasBorder: true,
          menuAlign: 'left',
          list: [
            {
              id: 1,
              items: listWarehouses,
            },
          ],
        },
        {
          order: 3,
          label: orderNumber ? `${t('order.orderNumberShort')}${orderNumber}` : t('nav.orders'),
          iconRight: ordersItems.length > 0 ? 'FiChevronDown' : undefined,
          hidden: !permissions.includes('VIEW_ORDER'),
          onScrollEnd: loadMoreOrders,
          menuAlign: 'left',
          triggerMinWidth: '10.5rem',
          list: [
            {
              id: 1,
              items: ordersItems,
            },
          ],
        },
        {
          order: 4,
          label: t('order.newOrder'),
          hidden: !permissions.includes('INSERT_ORDER'),
          onClick: () => {
            isOrderModalVisibleSet(true);
          },
        },
      ],
    },
    {
      id: 2,
      items: [
        {
          order: 1,
          label: formatSeasonLabel(season),
          iconRight: 'FiChevronDown',
          menuAlign: 'left',
          list: [
            {
              id: 1,
              items: seasonsItems,
            },
          ],
        },
        {
          order: 2,
          label: marketLine || '',
          iconRight: 'FiChevronDown',
          hasBorder: true,
          menuAlign: 'left',
          list: [
            {
              id: 1,
              items: linesItems || [],
            },
          ],
        },
        {
          order: 3,
          label: `${t('zone.label')} ${zone || ''}`,
          iconRight: 'FiChevronDown',
          hasBorder: true,
          menuAlign: 'left',
          list: [
            {
              id: 1,
              items: zonesItems,
            },
          ],
        },
        {
          order: 4,
          label: i18next.language,
          iconRight: 'FiChevronDown',
          hasBorder: true,
          menuAlign: 'left',
          list: [
            {
              id: 1,
              items: [
                {
                  label: '🇺🇸',
                  to: currentPage,
                  onClick: () => {
                    i18next.changeLanguage('en');
                    localStorage.setItem('language', 'en');
                    toast.success(t('settings.languageChanged'));
                  },
                },
                {
                  label: '🇮🇹',
                  to: currentPage,
                  onClick: () => {
                    i18next.changeLanguage('it');
                    localStorage.setItem('language', 'it');
                    toast.success(t('settings.languageChanged'));
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  ];
};

export { useSettingsData };
